/**
 * Planning doc blocks flattened to rows of styled spans, for renderers that draw text runs
 * rather than a DOM tree (the NativeScript Planning page: one Label per row, one Span per run).
 * US 35621. Every run is plain text plus flags; there is no markup to interpret.
 */
import {
  ParsedPlanningDoc,
  PlanningBlock,
  PlanningAttributionKind,
  PlanningInline,
  PlanningLine,
} from './planning-markdown.logic';

export interface PlanningSpan {
  text: string;
  /** Set only for a safe link (ticket ref, http(s), mailto or #anchor). */
  href: string | null;
  strong: boolean;
  em: boolean;
  code: boolean;
  /** Set on the leading "Decided (Tim)" badge run. */
  attribution: PlanningAttributionKind | null;
}

export type PlanningRow =
  | { kind: 'heading'; id: string; level: number; spans: PlanningSpan[] }
  | {
      kind: 'line';
      variant: 'paragraph' | 'bullet' | 'ordered' | 'quote';
      /** "•", "1." and so on; empty for paragraphs and quotes. */
      marker: string;
      tone: PlanningAttributionKind | 'plain';
      spans: PlanningSpan[];
    }
  | { kind: 'code'; text: string }
  | { kind: 'rule' };

const run = (
  text: string,
  extra: Partial<PlanningSpan> = {}
): PlanningSpan => ({
  text,
  href: null,
  strong: false,
  em: false,
  code: false,
  attribution: null,
  ...extra,
});

export function flattenInline(
  tokens: PlanningInline[],
  strong = false,
  em = false
): PlanningSpan[] {
  return tokens.flatMap((t): PlanningSpan[] => {
    switch (t.kind) {
      case 'strong':
        return flattenInline(t.children, true, em);
      case 'em':
        return flattenInline(t.children, strong, true);
      case 'code':
        return [run(t.text, { code: true, strong, em })];
      case 'link':
        return [run(t.text, { href: t.href, strong, em })];
      case 'image':
        // WI 35683: mobile draws text runs only, so an image reads as its alt text.
        return [run(t.alt ? `[Image: ${t.alt}]` : '[Image]', { strong, em })];
      default:
        return [run(t.text, { strong, em })];
    }
  });
}

export function lineSpans(line: PlanningLine): PlanningSpan[] {
  const body = flattenInline(line.inlines);
  if (!line.attribution) return body;
  const { kind, label, name } = line.attribution;
  return [
    run(`${label} (${name})`, { attribution: kind, strong: true }),
    run(' '),
    ...body,
  ];
}

function lineRow(
  line: PlanningLine,
  variant: 'paragraph' | 'bullet' | 'ordered' | 'quote',
  marker = ''
): PlanningRow {
  return {
    kind: 'line',
    variant,
    marker,
    tone: line.attribution?.kind ?? 'plain',
    spans: lineSpans(line),
  };
}

/** A table row as one text line, cells joined by " | " (mobile draws runs, not a grid). */
function tableRowSpans(cells: PlanningLine[], strong: boolean): PlanningSpan[] {
  return cells.flatMap((cell, i) => [
    ...(i ? [run(' | ')] : []),
    ...lineSpans(cell).map((span) => (strong ? { ...span, strong } : span)),
  ]);
}

export function planningRows(doc: ParsedPlanningDoc): PlanningRow[] {
  return blockRows(doc.blocks);
}

function blockRows(blocks: PlanningBlock[]): PlanningRow[] {
  return blocks.flatMap((block): PlanningRow[] => {
    switch (block.kind) {
      case 'heading':
        return [
          {
            kind: 'heading',
            id: block.id,
            level: block.level,
            spans: flattenInline(block.inlines),
          },
        ];
      case 'paragraph':
        return block.lines.map((line) => lineRow(line, 'paragraph'));
      case 'list':
        return block.items.map((item, i) =>
          block.ordered
            ? lineRow(item, 'ordered', `${i + 1}.`)
            : lineRow(item, 'bullet', '•')
        );
      case 'quote':
        return [lineRow(block.line, 'quote')];
      case 'code':
        return [{ kind: 'code', text: block.text }];
      case 'table':
        return [block.header, ...block.rows].map((cells, i): PlanningRow => ({
          kind: 'line',
          variant: 'paragraph',
          marker: '',
          // A body row takes its first attributed cell's tone, so a Decided cell still stands out.
          tone:
            i === 0
              ? 'plain'
              : (cells.find((cell) => cell.attribution)?.attribution?.kind ??
                'plain'),
          spans: tableRowSpans(cells, i === 0),
        }));
      case 'columns':
        // Mobile is one narrow column, so the columns stack in order.
        return block.columns.flatMap((column) => blockRows(column));
      default:
        return [{ kind: 'rule' }];
    }
  });
}
